import * as THREE from "three";
import { BAIL_TIME, type Skater } from "../skater";
import { GRIND_POSES, LIP_POSES, type FlipAnim, type ManualHoldPose } from "../tricks";
import { ArmSprings, clamp, POSES, Spring, type PoseName } from "./pose";
import { GRAB_SPOTS, GRAB_TUCK_DROP, GRAB_TUCK_LEAN, GRAB_TUCK_LIFT, GRAB_TWEAKS } from "./grab-pose";
import { MAX_HIP, MIN_HIP, reachTo, solveLeg } from "./ik";
import { buildRider, DEFAULT_LOOK, FOREARM, SHIN, THIGH, UPPER_ARM, type Arm, type Leg, type SkaterLook } from "./skater-parts";
import { WalkPose } from "./walk-pose";
import { HairSwing } from "./hair-swing";
import type { RideId } from "../sim/rides";
import { HeldRig } from "./rider/held-rig";
import { BikePose } from "./rider/bike-pose";
import { easeHold, type RideCue, type RideRig } from "./rider/ride-pose";
import { ScooterPose } from "./rider/scooter-pose";
import { SkatesPose } from "./rider/skates-pose";
import { raise, setArm } from "./arm";

// Poses the rider every frame from the sim's state; the meshes are built in
// skater-parts.ts. Model space: the board's nose points +z; the rider stands
// regular (left foot forward) so they face -x.

const STAND_HIP = 0.7;
/** Knees tucked at the top of an ollie: the hips drop and the board comes up. */
const AIR_TUCK_DROP = 0.06;
const AIR_TUCK_LIFT = 0.08;
/** How hard a landing sinks the hips (m/s of hip speed per m/s of impact), and the cap. */
const LAND_ABSORB = 0.35;
const LAND_ABSORB_MAX = 5;
/** The pop straightens the legs. */
const POP_EXTEND = 1.1;
/** How much the shoulders shrug up with a raised arm. */
const SHRUG = 0.045;
/** How far the shoulders open toward the direction of travel; the head turns the rest. */
const TWIST = 0.9;
/** How long a Boneless / Fastplant holds its pop pose before the normal air pose. */
const POP_POSE_TIME = 0.35;
/**
 * The Invert's handplant (#79): hips this high over the feet bends the knees
 * about 80 degrees; the back arches this far (radians); the planted arm is
 * this close to straight (a share of its length, shoulder to coping).
 */
const HANDPLANT_HIP = (THIGH + SHIN) * Math.cos(0.7);
const HANDPLANT_ARCH = 0.35;
const HANDPLANT_REACH = 0.995;
/** The planted arm reaches this far (radians) in front of the chest rather than straight past the head. */
const HANDPLANT_LEAN = 0.5;

export class SkaterModel {
  readonly root: THREE.Group;
  private tilt: THREE.Group;
  private yawGroup: THREE.Group;
  private board: THREE.Group;
  private boardSpin: THREE.Group;
  private rider: THREE.Group;
  private hips: THREE.Group;
  private torso: THREE.Group;
  private head: THREE.Group;
  private armFront: Arm;
  private armBack: Arm;
  private legs: Leg[];

  private up = new THREE.Vector3(0, 1, 0);
  private lean = 0;
  /** +1 facing the nose, -1 facing the tail (fakie), eased between. */
  private facing = 1;
  /** 0 regular, 1 switch (other foot forward): the rider turns 180 on the board. */
  private stance = 0;
  /** 0 normal, 1 weight over the nose for a nollie. */
  private nollieLean = 0;
  /** 0..1 blend into the wallplant pose. */
  private plant = 0;
  /** 0..1..0 through a Boneless / Fastplant's pop, and which one. */
  private popAmt = 0;
  private popKind: Skater["popVariant"] = null;
  /** -1 on the tail (manual), +1 on the nose (nose manual), eased between. */
  private manualTip = 0;
  /** The manual's balance needle, eased, for a little wobble. */
  private manualWobble = 0;
  /** 0..1 blend into a special grab's pose (Christ Air): body stretched out, board held up. */
  private christSpring = new Spring(0, 0.14);
  private get christ(): number {
    return clamp(this.christSpring.value, 0, 1);
  }
  /** 0..1 blend into a held ground trick's pose (Casper, Pogo...), and the last one held. */
  private hold = 0;
  /** 0..1 tucked up for a grab: knees pulled in and chest over the board so the hand reaches it. */
  private tuckSpring = new Spring(0, 0.1);
  private get tuck(): number {
    return clamp(this.tuckSpring.value, 0, 1);
  }
  /** 0..1 into the current grab's tweak, and which grab it was (kept while it eases out). */
  private tweakSpring = new Spring(0, 0.16);
  private tweakName: string | null = null;
  private get tweak(): number {
    return clamp(this.tweakSpring.value, 0, 1);
  }
  /** This frame's hip height, kept so the feet can follow the board to a grabbing hand. */
  private hipY = STAND_HIP;
  /** How far a grab's tweak has moved each foot's spot on the deck (rider space, metres), so the feet stay on it. */
  private footShift = [
    { y: 0, z: 0 },
    { y: 0, z: 0 },
  ];
  private holdPose: ManualHoldPose | null = null;
  private slideYaw = 0;
  /** The grind's board tip onto a truck and edge dip, eased. */
  private grindPitch = 0;
  private grindRoll = 0;
  /** Upside down on the coping (Invert), eased 0..1. */
  private invert = 0;
  /**
   * The share of `invert` that's a handplant on the coping (a lip Invert, not
   * a McTwist in the air), and the coping it's planted on, kept while it eases out.
   */
  private handplant = 0;
  private plantedOnLip = false;
  private coping = new THREE.Vector3();
  /** The last planted frame (where the rider hung and how the arm was), to ease out of after dropping in. */
  private plantedRoot = new THREE.Vector3();
  private plantedArm = [new THREE.Quaternion(), new THREE.Quaternion()] as const;
  /** The free arm pose (no hand on anything) chases a named target through these. */
  private armSprings = { front: new ArmSprings(POSES.roll.front), back: new ArmSprings(POSES.roll.back) };
  /** Hip height over the feet: crouch, soft knees, the pop and the air tuck all go through it. */
  private hipSpring = new Spring(STAND_HIP - POSES.roll.kneeSoft, 0.05);
  /** How far a landing has sunk the hips, metres below where they'd be (<= 0); kicked on touchdown, springs back. */
  private landDip = new Spring(0, 0.12);
  /** 0..1 knees tucked up at the top of an air. */
  private airTuck = new Spring(0, 0.12);
  /** 0..1 a hand reaching for the board (grabs, Boneless, Pogo), and where it last went. */
  private reach = new Spring(0, 0.15);
  private reachSpot: string | null = null;
  /** Arms wound against a turn or leading a spin, radians; the arm springs ease it. */
  private wind = 0;
  private swayT = 0;
  private prevMode: Skater["mode"] = "ground";
  private prevVy = 0;
  private prevYaw = 0;
  /** Last frame's arm raise, for the shrug. */
  private raised = { front: 0, back: 0 };
  private poseName: PoseName = "roll";
  /** The rider on foot (#19), blended over the board pose. */
  private walk: WalkPose;
  /** Long hair's swing (#227), for a look with long hair or a ponytail (#234). */
  readonly hair: HairSwing | null;
  /** What's in the hands on foot (#153): the gun or the washer's wand, and the board on the back. */
  private heldRig: HeldRig;
  /**
   * The rides from the pack, each posed after the board's in this order:
   * the BMX (#141, `rider/bike-pose.ts`), the scooter (#139) and the
   * skates (#140). `hold` is 0..1 how far it's out on foot (walked beside
   * you, or the skates slung), eased; `handOff` shares the hand out.
   */
  private rides: { id: RideId; pose: RideRig; hold: number }[];
  private readonly bikePose: BikePose;
  private readonly scooterPose: ScooterPose;
  private readonly skatesPose: SkatesPose;
  /** What the rides' poses read off the board pose each frame. */
  private rideCue: RideCue = { lean: 0, manualTip: 0, manualWobble: 0, airTuck: 0, landDip: 0 };
  private dt = 0;
  private shoulderY: number;
  private tmpQ = new THREE.Quaternion();
  private tmpQ2 = new THREE.Quaternion();

  constructor(look: SkaterLook = DEFAULT_LOOK) {
    const rig = buildRider(look);
    this.root = rig.root;
    this.tilt = rig.tilt;
    this.yawGroup = rig.yawGroup;
    this.board = rig.board;
    this.boardSpin = rig.boardSpin;
    this.rider = rig.rider;
    this.hips = rig.hips;
    this.torso = rig.torso;
    this.head = rig.head;
    this.armFront = rig.armFront;
    this.armBack = rig.armBack;
    this.legs = rig.legs;
    this.shoulderY = this.armFront.shoulder.position.y;
    this.walk = new WalkPose(rig);
    this.hair = HairSwing.find(rig.head, rig.torso);
    this.heldRig = new HeldRig(rig);
    this.bikePose = new BikePose(rig, look);
    this.scooterPose = new ScooterPose(rig, look);
    this.skatesPose = new SkatesPose(rig, look);
    this.rides = [
      { id: "bmx", pose: this.bikePose, hold: 0 },
      { id: "scooter", pose: this.scooterPose, hold: 0 },
      { id: "skates", pose: this.skatesPose, hold: 0 },
    ];
  }

  /** What a bail style (#83) can knock off the body: the head, both arms, both legs and the board. */
  get limbs(): THREE.Object3D[] {
    return [this.head, this.armFront.shoulder, this.armBack.shoulder, ...this.legs.map((l) => l.hip), this.board];
  }

  /** Copies simulation state onto the model. Call once per rendered frame. */
  update(sk: Skater, dt: number, turnInput: number): void {
    const k = (rate: number) => 1 - Math.exp(-rate * dt);
    this.root.position.set(sk.x, sk.y, sk.z);

    // Align to the ground normal, easing toward world up while airborne. A
    // wallride's normal tips the board against the wall, rider leaning out.
    const target = new THREE.Vector3(sk.normal.x, sk.normal.y, sk.normal.z);
    this.up.lerp(target, k(sk.mode === "air" && !sk.wallride ? 4 : 18)).normalize();
    this.tmpQ.setFromUnitVectors(new THREE.Vector3(0, 1, 0), this.up);
    this.tilt.quaternion.copy(this.tmpQ);

    // Slides turn the whole skater across the rail; angled grinds (Crooked,
    // Smith...) turn it part way. The board's tip onto a truck is in poseBoard.
    // Lip tricks borrow the same yaw / pitch / roll, plus the Invert's handplant.
    const lipPose = sk.mode === "lip" && sk.lip ? LIP_POSES[sk.lip.trick.style] : null;
    const pose = sk.mode === "grind" && sk.grind ? GRIND_POSES[sk.grind.trick.style] : lipPose;
    this.slideYaw += ((pose?.yaw ?? 0) - this.slideYaw) * k(20);
    this.grindPitch += ((pose?.pitch ?? 0) - this.grindPitch) * k(20);
    this.grindRoll += ((pose?.roll ?? 0) - this.grindRoll) * k(20);
    // A revert flips the sim's heading instantly; swing the model through it.
    const pivot = Math.min(1, sk.pivotT / 0.22);
    const pivotLag = -Math.PI * (1 - pivot * pivot * (3 - 2 * pivot));
    this.tmpQ2.setFromAxisAngle(new THREE.Vector3(0, 1, 0), sk.yaw + this.slideYaw + pivotLag);
    this.yawGroup.quaternion.copy(this.tmpQ2);
    // Invert: flip over onto one hand on the coping, board held up overhead.
    // A McTwist goes over and back round in its own time, so it follows the trick directly.
    const timed = sk.grab?.timed;
    if (timed?.invert) this.invert = Math.sin(Math.PI * Math.min(1, sk.grab!.t / timed.duration));
    else this.invert += ((lipPose?.invert ?? 0) - this.invert) * k(10);
    // On the lip it's a handplant: `plantHand` puts the front hand on the coping and hangs the rider off it.
    if (sk.mode === "lip") {
      this.plantedOnLip = true;
      this.coping.set(sk.x, sk.y, sk.z);
    } else if (timed?.invert) this.plantedOnLip = false;
    this.handplant = this.plantedOnLip ? this.invert : 0;
    if (this.invert > 0.01) {
      this.tmpQ.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -Math.PI * this.invert);
      this.yawGroup.quaternion.multiply(this.tmpQ);
      if (this.handplant <= 0.01) this.root.position.y += 1.5 * this.invert;
    }
    // A bike special's flip (#138): head over heels about the hips, rider and
    // bike together, in the special's own time (backward when `flips` > 0).
    if (timed && timed.flips !== 0) {
      const p = Math.min(1, sk.grab!.t / timed.duration);
      const a = -2 * Math.PI * timed.flips * p * p * (3 - 2 * p);
      TMP_V.set(0, FLIP_PIVOT_Y * (1 - Math.cos(a)), -FLIP_PIVOT_Y * Math.sin(a));
      this.root.position.add(TMP_V.applyQuaternion(this.yawGroup.quaternion).applyQuaternion(this.tilt.quaternion));
      this.yawGroup.quaternion.multiply(this.tmpQ.setFromAxisAngle(FLIP_AXIS, a));
    }

    // Wallplant: snap into it, ease out after the kick-off. The skater leans
    // back off the wall with the board tipped nose-up against it.
    this.plant += ((sk.plantProgress !== null ? 1 : 0) - this.plant) * k(sk.plantProgress !== null ? 30 : 8);
    if (this.plant > 0.01) {
      this.tmpQ.setFromAxisAngle(new THREE.Vector3(1, 0, 0), -0.35 * this.plant);
      this.yawGroup.quaternion.multiply(this.tmpQ);
    }

    // Lean into turns on the ground; wobble with the balance needle on rails.
    let leanTarget = 0;
    if (sk.mode === "ground") leanTarget = turnInput * 0.25 * Math.min(1, sk.horizontalSpeed / 5);
    if (sk.mode === "grind" && sk.grind) leanTarget = -sk.grind.balance * 0.55;
    if (sk.mode === "lip" && sk.lip) leanTarget = -sk.lip.balance * 0.55;
    this.lean += (leanTarget - this.lean) * k(10);

    const travel = sk.travelSign;
    if (travel !== 0) this.facing += (travel - this.facing) * k(6);
    this.stance += ((sk.switchStance ? 1 : 0) - this.stance) * k(12);
    this.nollieLean += ((sk.nollie ? 1 : 0) - this.nollieLean) * k(12);
    const tipTarget = sk.manual ? (sk.manual.trick.nose ? 1 : -1) : 0;
    this.manualTip += (tipTarget - this.manualTip) * k(16);
    this.manualWobble += ((sk.manual?.balance ?? 0) - this.manualWobble) * k(20);
    // A special takes its time to open up, and snaps back when you let go.
    const christ = sk.grab?.special && !sk.grab.timed && sk.grab.released === null;
    this.christSpring.update(christ ? 1 : 0, dt);
    // Boneless / Fastplant: a quick hand-on-the-board pop, then back to the air pose.
    const popping = sk.mode === "air" && sk.popVariant && !sk.grab && !sk.flip;
    this.popAmt = popping ? Math.sin(Math.PI * Math.min(1, sk.popT / POP_POSE_TIME)) : 0;
    this.popKind = popping ? sk.popVariant : null;
    const held = sk.manual?.hold?.trick.pose ?? null;
    if (held) this.holdPose = held;
    this.hold += ((held ? 1 : 0) - this.hold) * k(held ? 14 : 18);
    const tucked = (sk.grab !== null && !christ && sk.grab.released === null) || this.popAmt > 0.3;
    this.tuckSpring.update(tucked ? 1 : 0, dt);
    // The tweak waits for the hand to reach the board, then eases in; letting go eases it out.
    const grabName = sk.grab && !sk.grab.special && !sk.grab.timed && sk.grab.released === null ? sk.grab.name : null;
    if (grabName && GRAB_TWEAKS[grabName]) {
      if (grabName !== this.tweakName && this.tweak > 0.05) this.tweakSpring.update(0, dt);
      else {
        this.tweakName = grabName;
        this.tweakSpring.update(this.reach.value > 0.8 ? 1 : 0, dt);
      }
    } else this.tweakSpring.update(0, dt);
    this.drive(sk, dt, !!christ);

    this.poseBody(sk);
    this.poseBoard(sk);
    this.walk.apply(sk, this.hipY);
    this.poseArms(sk);
    if (this.heldRig.pose(this.root, this.armBack, this.armFront)) {
      this.raised.front = raise(this.armFront);
      this.raised.back = raise(this.armBack);
    }
    if (this.handplant > 0.01) this.plantHand(this.handplant, sk.mode === "lip");
    if (this.plant > 0.01) this.boardSpin.rotation.x -= 0.9 * this.plant;
    const cue = this.rideCue;
    cue.lean = this.lean;
    cue.manualTip = this.manualTip;
    cue.manualWobble = this.manualWobble;
    cue.airTuck = this.airTuck.value;
    cue.landDip = this.landDip.value;
    let armsMoved = false;
    for (let i = 0; i < this.rides.length; i++) {
      const pose = this.rides[i]!.pose;
      const walked = pose.walkBeside(sk, this.dt, this.handOff(i));
      const on = pose.apply(sk, this.dt, cue);
      armsMoved = armsMoved || walked || on;
    }
    if (armsMoved) {
      this.raised.front = raise(this.armFront);
      this.raised.back = raise(this.armBack);
    }
  }

  /**
   * Kicks and targets for the springs: landings sink the hips and drop the
   * arms, the pop straightens the legs, knees tuck at the top of an air, a
   * hand reaches for the board, and the arms lag turns and whip through spins.
   */
  private drive(sk: Skater, dt: number, christ: boolean): void {
    const landed = this.prevMode === "air" && (sk.mode === "ground" || sk.mode === "grind" || sk.mode === "lip");
    if (landed) {
      // The deeper the drop, the deeper the absorb.
      const absorb = Math.min(LAND_ABSORB_MAX, LAND_ABSORB * Math.max(0, -this.prevVy));
      this.landDip.vel -= absorb;
      for (const a of [this.armSprings.front, this.armSprings.back]) {
        a.out.vel -= 1.5 * absorb;
        a.bend.vel += absorb;
      }
    }
    if (this.prevMode !== "air" && sk.mode === "air" && sk.vy > 1) this.hipSpring.vel += POP_EXTEND;
    // Knees tuck as the air peaks, and let go on the way down.
    const freeAir = sk.mode === "air" && !sk.grab && !sk.wallride && sk.plantProgress === null;
    this.airTuck.update(freeAir ? 1 - Math.min(1, Math.abs(sk.vy) / 5) : 0, dt);

    let spot: string | null = null;
    if (sk.grab?.timed) spot = sk.grab.timed.hand;
    else if (sk.grab && !sk.grab.special && sk.grab.released === null) spot = sk.grab.name;
    // A Pogo holds the nose; Boneless: back hand on the board's middle, Fastplant: front hand on the nose.
    else if (this.holdPose?.hand && this.hold > 0.5) spot = this.holdPose.hand === "front" ? "Nosegrab" : "Tailgrab";
    else if (this.popAmt > 0.3) spot = this.popKind === "fastplant" ? "Nosegrab" : "Indy";
    if (spot && spot !== this.reachSpot && this.reach.value > 0.05) {
      // Switching hands mid-reach: let go first.
      this.reach.update(0, dt);
    } else {
      if (spot) this.reachSpot = spot;
      this.reach.update(spot ? 1 : 0, dt);
    }
    if (christ) this.reach.set(0);

    // Arms lag behind a turn, wind up against it while crouched, and lead
    // through a spin in the air (arms first, so they whip round).
    let dYaw = sk.yaw - this.prevYaw;
    dYaw = Math.atan2(Math.sin(dYaw), Math.cos(dYaw));
    const yawRate = Math.abs(dYaw) < 0.5 && dt > 0 ? dYaw / dt : 0;
    const windTarget =
      sk.mode === "air" ? clamp(0.09 * yawRate, -0.8, 0.8) : sk.mode === "ground" ? clamp(-(0.05 + 0.12 * sk.crouch) * yawRate, -0.5, 0.5) : 0;
    this.wind = windTarget;
    this.swayT += dt * (sk.mode === "ground" ? Math.min(1.5, 0.4 + sk.horizontalSpeed / 10) : 0);

    this.prevMode = sk.mode;
    this.prevYaw = sk.yaw;
    if (sk.mode === "air") this.prevVy = sk.vy;
    this.walk.ease(sk, dt);
    this.heldRig.ease(sk, dt);
    // A ride out on foot: the BMX (#145) and the scooter (#139) walked
    // beside you, a hand on the bars; the skates (#140) slung over the shoulder.
    const out = this.heldRig.walking;
    for (const r of this.rides) r.hold = easeHold(r.hold, out === r.id, 10, dt);
    this.poseName =
      sk.mode === "bail"
        ? "bail"
        : sk.mode === "foot"
          ? sk.foot?.ledge
            ? "hang"
            : "walk"
          : sk.skitch
          ? "skitch"
          : sk.mode === "grind" || sk.mode === "lip" || sk.manual
            ? "balance"
            : sk.mode === "air"
              ? "air"
              : "roll";
    this.dt = dt;
    // Last, once the body's pose is set: the hair hangs from where the head ended up.
    this.hair?.update(dt);
  }

  private poseBody(sk: Skater): void {
    const P = POSES[this.poseName];
    // The knees fold as the crouch builds, eased like everything else.
    const hipTarget = STAND_HIP - P.kneeSoft - 0.3 * sk.crouch - AIR_TUCK_DROP * this.airTuck.value;
    const spring = this.hipSpring.update(hipTarget, this.dt);
    if (spring > MAX_HIP) this.hipSpring.set(MAX_HIP);
    const dip = this.landDip.update(0, this.dt);
    let hipY = Math.max(MIN_HIP, Math.min(MAX_HIP, spring) + dip - GRAB_TUCK_DROP * this.tuck);
    // Handplant: knees bent well over, feet (and board) above the hips.
    hipY += (HANDPLANT_HIP - hipY) * this.handplant;
    this.hips.position.set(0, hipY, 0.08 * this.nollieLean);
    // Lean over the toes, then open the shoulders toward where we're rolling;
    // the head counter-leans to stay upright and turns to look down the line.
    // Switch stance turns the rider 180 on the board, which mirrors which way
    // "toward the nose" is for the twist.
    const stanceYaw = Math.PI * this.stance;
    const side = Math.cos(stanceYaw);
    const tweakLean = (this.tweakName ? GRAB_TWEAKS[this.tweakName]!.lean : 0) * this.tweak;
    this.torso.rotation.set(0, TWIST * this.facing * side, 0.12 + 0.4 * sk.crouch + GRAB_TUCK_LEAN * this.tuck + tweakLean, "YZX");
    this.head.rotation.set(0, (Math.PI / 2 - TWIST) * this.facing * side, -0.1 - 0.35 * sk.crouch, "ZYX");
    // Tucked for a grab, the shoulders square up over the board.
    this.torso.rotation.y *= 1 - 0.6 * this.tuck;
    // Handplant: square to the coping, back arched.
    this.torso.rotation.y *= 1 - this.handplant;
    this.torso.rotation.z -= (HANDPLANT_ARCH + 0.4 * sk.crouch) * this.handplant;
    // Manuals: weight over the wheels that are down, leaning back (or over the
    // nose), rocking with the balance needle. X turns before the stance yaw,
    // so flip it with the stance to keep leaning toward the same end of the board.
    const tip = this.manualTip;
    const h = this.holdPose ? this.hold : 0;
    const manualLean = (0.22 * tip + 0.25 * this.manualWobble * Math.abs(tip)) * side * (1 - 0.6 * h);
    // Held ground tricks: stand up on top of wherever the board has gone.
    this.rider.position.set(0, 0.12 + (this.holdPose?.lift ?? 0) * h, 0.1 * tip * (1 - h));
    this.rider.rotation.set(manualLean, stanceYaw, this.lean, "ZYX");
    // Christ Air: feet off the board, body straight and square, head back.
    const c = this.christ;
    if (c > 0.01) {
      this.rider.position.y += 0.35 * c;
      this.torso.rotation.y *= 1 - c;
      this.torso.rotation.z *= 1 - c;
      this.head.rotation.z += 0.35 * c;
    }

    if (sk.mode === "bail") {
      // Slam: fall toward the heels, then pick yourself up at the end.
      const fall = Math.min(1, sk.bailT / 0.3);
      const getUp = Math.max(0, (sk.bailT - (BAIL_TIME - 0.35)) / 0.35);
      const p = fall * (1 - getUp);
      this.rider.rotation.set(0, stanceYaw, -1.45 * p * side, "ZYX");
      this.rider.position.set(0.35 * p * side, 0.12 - 0.05 * p, 0);
      this.torso.rotation.z = 0.12 - 0.3 * p;
    }
    // A grab tuck pulls the board up to the rider, knees folding to follow it.
    this.hipY = hipY;
    this.board.position.set(0, 0.1 + GRAB_TUCK_LIFT * this.tuck + AIR_TUCK_LIFT * this.airTuck.value, 0);
    this.plantFeet();
  }

  /** Plants the feet on the board wherever it has been raised to. */
  private plantFeet(): void {
    this.legs.forEach((leg, i) => {
      const shift = this.footShift[i]!;
      solveLeg(leg, this.hipY - (this.board.position.y - 0.1) - shift.y, shift.z);
    });
  }

  private poseBoard(sk: Skater): void {
    const s = this.boardSpin;
    s.rotation.set(0, 0, 0);
    s.position.set(0, 0, 0);
    this.board.visible = true;
    if (sk.mode === "foot") {
      this.carryBoard();
      return;
    }
    if (sk.flip) this.spinBoard(sk.flip.trick.anim, sk.flip.trick.turns, Math.min(1, sk.flip.t / sk.flip.duration));
    if ((sk.mode === "grind" && sk.grind) || sk.mode === "lip") {
      // Pivot on the truck that's on the rail, so it stays put as the other end dips.
      const pitch = this.grindPitch;
      s.rotation.set(pitch, 0, this.grindRoll);
      s.position.set(0, 0.2 * Math.sin(Math.abs(pitch)), 0);
    }
    if (Math.abs(this.manualTip) > 0.01) {
      // Tipped onto the tail (or nose), pivoting on the wheels that stay down.
      // A held ground trick swaps that for its own pose: over onto its back
      // (Casper), up on its edge (Primo), stood on end (Pogo).
      const pose = this.holdPose;
      const h = pose ? this.hold : 0;
      const tipAngle = 0.32 * this.manualTip + 0.08 * this.manualWobble * Math.abs(this.manualTip);
      const angle = tipAngle + ((pose?.pitch ?? 0) - tipAngle) * h;
      s.rotation.x += angle;
      s.rotation.z += (pose?.roll ?? 0) * h;
      s.position.y += 0.2 * Math.sin(Math.abs(angle));
      // Stood on end, the board sits in front of the rider's toes rather than through the legs.
      s.position.x -= 0.16 * Math.sin(Math.abs(angle)) ** 2 * h;
      const pop = sk.manual?.pop;
      if (pop) this.spinBoard(pop.trick.anim, pop.trick.rotations, Math.min(1, pop.t / pop.duration), 0.12);
    }
    const tw = this.tweak;
    if (tw > 0.001 && this.tweakName) {
      // Turned about the hand's spot, as the legs push the board round the
      // hand that holds it, so the arm doesn't have to chase it.
      const g = GRAB_TWEAKS[this.tweakName]!;
      const spot = GRAB_SPOTS[this.tweakName]![1];
      const fromPos = TWEAK_FROM_P.copy(s.position);
      const fromQuat = TWEAK_FROM_Q.copy(s.quaternion);
      const before = TMP_V.copy(spot).applyQuaternion(s.quaternion);
      s.position.add(before);
      s.rotation.set(s.rotation.x + g.pitch * tw, s.rotation.y + g.yaw * tw, s.rotation.z + g.roll * tw);
      s.position.sub(TMP_V.copy(spot).applyQuaternion(s.quaternion));
      s.position.x += g.push[0] * tw;
      s.position.y += g.push[1] * tw;
      s.position.z += g.push[2] * tw;
      // Each foot rides its own spot on the tweaked deck: one leg bones out
      // as the other folds. Switch stance turns the rider's z round on the board.
      const along = Math.cos(Math.PI * this.stance);
      this.legs.forEach((leg, i) => {
        const z = leg.z * along;
        const moved = TMP_V.set(0, 0, z).applyQuaternion(s.quaternion).add(s.position);
        moved.sub(TMP_V2.set(0, 0, z).applyQuaternion(fromQuat).add(fromPos));
        const shift = this.footShift[i]!;
        shift.y = moved.y;
        shift.z = moved.z * along;
      });
    } else for (const shift of this.footShift) shift.y = shift.z = 0;
    this.plantFeet();
    if (this.popAmt > 0.01) {
      // Jumped up nose-first off the planted foot; a Fastplant rolls it onto its heel edge too.
      const a = this.popAmt;
      s.rotation.x -= 0.55 * a;
      if (this.popKind === "fastplant") s.rotation.z += 0.3 * a;
      s.position.y += 0.15 * a;
    }
    if (sk.mode === "bail") {
      // The board squirts out ahead and tumbles.
      const t = sk.bailT;
      s.position.set(0, Math.max(0, Math.sin(Math.min(t * 5, Math.PI)) * 0.4), Math.min(2.5, t * 3));
      s.rotation.set(t * 7, 0, t * 4);
      if (t > BAIL_TIME - 0.3) {
        s.position.set(0, 0, 0);
        s.rotation.set(0, 0, 0);
      }
    }
    // Just back on: the board comes down from under the arm (after the feet are on it, so they don't chase it).
    if (this.walk.blend > 0) this.carryBoard();
  }

  /** Carried under the back arm on foot, part way there while blending (#19); on the back with anything else out (#153). */
  private carryBoard(): void {
    this.walk.carryBoard(this.board, this.boardSpin, Math.sign(this.armBack.shoulder.position.z), this.heldRig.packed);
  }

  /**
   * How much of a hand ride `i` gets walking it on foot: the bike takes it
   * as it comes out; the scooter and the skates only as the gun and the
   * bike are put away, so switching straight from one to the other, the arm
   * swings right round (#139, #140). Going back in the pack, a ride lets go
   * at its own pace, not faster for the gun or the washer coming up (#151):
   * both falling at once snapped the arm.
   */
  private handOff(i: number): number {
    const r = this.rides[i]!;
    if (i === 0) return r.hold;
    const gun = this.heldRig.walking === r.id ? 1 - this.heldRig.hold : 1;
    return r.hold * gun * (1 - this.rides[0]!.hold);
  }

  /** Turns the board through a flip or shove-it, `p` 0..1 of the way, popped `hop` up. */
  private spinBoard(anim: FlipAnim, n: number, p: number, hop = 0.18): void {
    const s = this.boardSpin;
    const turn = Math.PI * 2 * n * p;
    s.position.y += Math.sin(Math.PI * p) * hop;
    switch (anim) {
      case "kickflip":
        s.rotation.z += turn;
        break;
      case "heelflip":
        s.rotation.z -= turn;
        break;
      case "varial":
        s.rotation.set(s.rotation.x, Math.PI * n * p, turn, "YXZ");
        break;
      case "treflip":
        // 360 Flip: the kickflip plus a full backside shove.
        s.rotation.set(s.rotation.x, turn, turn, "YXZ");
        break;
      case "hardflip":
        // A front shove, so the board flips up between the legs.
        s.rotation.set(s.rotation.x, -Math.PI * n * p, turn, "YXZ");
        break;
      case "varialheel":
        s.rotation.set(s.rotation.x, -Math.PI * n * p, -turn, "YXZ");
        break;
      case "impossible":
        s.rotation.x += turn;
        break;
      case "shove":
        s.rotation.y += Math.PI * n * p;
        break;
      case "underflip":
        // Kickflipped over, then flipped back under the other way.
        s.rotation.z += Math.PI * 2 * n * (p < 0.5 ? 2 * p : 2 - 2 * p);
        break;
    }
  }

  private poseArms(sk: Skater): void {
    const f = this.armFront;
    const b = this.armBack;
    const dt = this.dt;
    // Shoulders shrug up with a raised arm (from last frame, so the grab IK sees where they are).
    f.shoulder.position.y = this.shoulderY + SHRUG * shrug(this.raised.front);
    b.shoulder.position.y = this.shoulderY + SHRUG * shrug(this.raised.back);
    if (sk.mode === "bail") {
      // Flailing: arms thrown up, elbows pumping out of step with each other. Set outright.
      const P = POSES.bail;
      const front = { ...P.front, bend: P.front.bend + 0.6 * Math.sin(sk.bailT * 17) };
      const back = { ...P.back, bend: P.back.bend + 0.6 * Math.sin(sk.bailT * 17 + 2) };
      this.armSprings.front.set(front);
      this.armSprings.back.set(back);
      setArm(f, -1, front);
      setArm(b, 1, back);
    } else {
      const P = POSES[this.poseName];
      const front = { ...P.front };
      const back = { ...P.back };
      // Balancing: the arms go up as far as you're off balance, see-sawing with the needle.
      const balance = sk.grind?.balance ?? sk.lip?.balance ?? sk.manual?.balance ?? 0;
      if (this.poseName === "balance") {
        const flail = (sk.grind?.balance ?? sk.lip?.balance ?? 0) * 0.5;
        front.out += 0.45 * Math.abs(balance) + flail;
        back.out += 0.45 * Math.abs(balance) - flail;
        front.bend += 0.35 * balance;
        back.bend -= 0.35 * balance;
      } else if (this.poseName === "skitch" && sk.skitch) {
        // Only the free arm balances: the other one's holding on.
        back.out += 0.6 * Math.abs(sk.skitch.balance);
        back.bend -= 0.3 * sk.skitch.balance;
      }
      if (this.poseName === "walk" && sk.foot) {
        // The free arm swings against its side's leg (the front leg is legs[0]).
        front.back += this.walk.armSwing(sk.foot);
      } else if (this.poseName === "hang" && sk.foot?.ledge) {
        // Climbing, the arm bends as it pulls you up.
        front.bend += 1.6 * Math.max(0, Math.min(1, sk.foot.ledge.climb / 0.6));
      }
      // Wind and sway swing the arms forward and back along the board, opposite each other.
      const sway = this.poseName === "roll" ? 0.07 : 0;
      front.back += this.wind + sway * Math.sin(2.2 * this.swayT);
      back.back -= this.wind - sway * Math.sin(2.2 * this.swayT + 1.3);
      front.out += 0.4 * sway * Math.sin(1.7 * this.swayT);
      back.out += 0.4 * sway * Math.sin(1.7 * this.swayT + 2);
      setArm(f, -1, this.armSprings.front.update(front, dt));
      setArm(b, 1, this.armSprings.back.update(back, dt));
    }
    if (this.reach.value > 0.001 && this.reachSpot) this.reachForBoard(this.reachSpot, Math.min(1, this.reach.value));
    if (this.christ > 0.01) this.holdBoardUp(this.christ);
    this.raised.front = raise(f);
    this.raised.back = raise(b);
  }

  /**
   * The Invert's handplant (#79): the front hand on the coping (where the sim
   * holds the skater during a lip trick), the arm straight and locked, and the
   * upside-down rider hung above it. `w` 0..1 blends it in from the flip.
   */
  private plantHand(w: number, onLip: boolean): void {
    const arm = this.armFront;
    const free = [arm.shoulder.quaternion.clone(), arm.elbow.quaternion.clone()] as const;
    if (onLip) {
      const coping = this.coping;
      this.root.updateMatrixWorld(true);
      // Move the whole rider so the shoulder sits an arm's length above the
      // hand, a little behind it so the arm reaches out in front of the chest.
      const shoulder = arm.shoulder.getWorldPosition(new THREE.Vector3());
      const chest = new THREE.Vector3(-1, 0, 0).transformDirection(this.torso.matrixWorld).setY(0);
      if (chest.lengthSq() > 1e-6) chest.normalize();
      const len = HANDPLANT_REACH * (UPPER_ARM + FOREARM);
      const want = coping
        .clone()
        .add(new THREE.Vector3(0, len * Math.cos(HANDPLANT_LEAN), 0))
        .addScaledVector(chest, len * Math.sin(HANDPLANT_LEAN));
      this.root.position.addScaledVector(want.sub(shoulder), w);
      this.root.updateMatrixWorld(true);
      // Always the locked arm's length, pointed at the coping: on it once
      // the rider is all the way over, and never folding on the way.
      const from = arm.shoulder.getWorldPosition(new THREE.Vector3());
      const dir = coping.clone().sub(from);
      reachTo(arm, dir.lengthSq() > 1e-8 ? from.addScaledVector(dir.normalize(), len) : coping);
      this.plantedRoot.copy(this.root.position);
      this.plantedArm[0].copy(arm.shoulder.quaternion);
      this.plantedArm[1].copy(arm.elbow.quaternion);
    } else {
      // Dropped back in: glide from where the rider hung, letting go of the coping.
      this.root.position.lerp(this.plantedRoot, w);
      arm.shoulder.quaternion.copy(this.plantedArm[0]);
      arm.elbow.quaternion.copy(this.plantedArm[1]);
    }
    if (w < 1) {
      arm.shoulder.quaternion.copy(free[0].slerp(arm.shoulder.quaternion, w));
      arm.elbow.quaternion.copy(free[1].slerp(arm.elbow.quaternion, w));
    }
    this.raised.front = raise(arm);
  }

  /**
   * Christ Air: arms out in a cross, the front hand holding the board up by
   * its tail truck, nose to the sky. `c` blends from the current pose.
   */
  private holdBoardUp(c: number): void {
    const down = new THREE.Vector3(0, -1, 0);
    const front = new THREE.Quaternion().setFromUnitVectors(down, new THREE.Vector3(0, 0.45, 1).normalize());
    const back = new THREE.Quaternion().setFromUnitVectors(down, new THREE.Vector3(0, 0.1, -1).normalize());
    const straight = new THREE.Quaternion();
    this.armFront.shoulder.quaternion.slerp(front, c);
    this.armBack.shoulder.quaternion.slerp(back, c);
    this.armFront.elbow.quaternion.slerp(straight, c);
    this.armBack.elbow.quaternion.slerp(straight, c);
    this.root.updateMatrixWorld(true);
    const hand = this.board.worldToLocal(this.armFront.hand.getWorldPosition(new THREE.Vector3()));
    // Standing on end (nose up), the tail truck sits 0.2 below the board's middle.
    hand.y += 0.2;
    const s = this.boardSpin;
    s.position.lerp(hand, c);
    s.rotation.x += (-Math.PI / 2 - s.rotation.x) * c;
  }

  /**
   * Puts one hand on a spot on the deck for the current grab, `w` 0..1 of the
   * way there from the arm's free pose.
   */
  private reachForBoard(grab: string, w: number): void {
    const [side, local] = GRAB_SPOTS[grab] ?? GRAB_SPOTS.Indy!;
    this.root.updateMatrixWorld(true);
    const arm = side === "front" ? this.armFront : this.armBack;
    const free = [arm.shoulder.quaternion.clone(), arm.elbow.quaternion.clone()] as const;
    const target = this.boardSpin.localToWorld(local.clone());
    reachTo(arm, target);
    // Out of reach: bring the board the rest of the way to the hand.
    this.root.updateMatrixWorld(true);
    const frame = this.board.parent!;
    const hand = frame.worldToLocal(arm.hand.getWorldPosition(new THREE.Vector3()));
    const gap = hand.sub(frame.worldToLocal(target)).multiplyScalar(w);
    if (gap.lengthSq() > 1e-8) {
      this.board.position.add(gap);
      this.plantFeet();
    }
    if (w < 1) {
      arm.shoulder.quaternion.copy(free[0].slerp(arm.shoulder.quaternion, w));
      arm.elbow.quaternion.copy(free[1].slerp(arm.elbow.quaternion, w));
    }
  }
}

/** 0 with the arm below about 35 degrees, 1 straight up. */
function shrug(raised: number): number {
  return clamp((raised - 0.6) / 2, 0, 1);
}

const TMP_V = new THREE.Vector3();
const TMP_V2 = new THREE.Vector3();
/** A body flip (#138) turns about the rider's side-to-side axis, this high over the wheels (about the hips). */
const FLIP_AXIS = new THREE.Vector3(1, 0, 0);
const FLIP_PIVOT_Y = 0.95;
/** The board's pose before this frame's grab tweak. */
const TWEAK_FROM_P = new THREE.Vector3();
const TWEAK_FROM_Q = new THREE.Quaternion();
